import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowUpRight, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { createLead } from "@/lib/api";

const TYPES = [
  { id: 'residential', label: 'Residential' },
  { id: 'commercial', label: 'Commercial' },
  { id: 'industrial', label: 'Industrial' },
  { id: 'mixed', label: 'Mixed-use' },
];

const LOCATIONS = [
  { id: 'ontario', label: 'City of Ontario' },
  { id: 'chino', label: 'City of Chino' },
  { id: 'sb', label: 'San Bernardino Co.' },
  { id: 'rv', label: 'Riverside Co.' },
];

const STATUSES = [
  { id: 'raw', label: 'Raw land', baseMonths: 18 },
  { id: 'preapp', label: 'Pre-application', baseMonths: 14 },
  { id: 'inprocess', label: 'Plan in process', baseMonths: 8 },
  { id: 'approved', label: 'Approved · ready to build', baseMonths: 3 },
];

// Phases the user can include in their entitlement scope.
const PHASES = [
  { id: 'feasibility', label: 'Feasibility' },
  { id: 'specific', label: 'Specific Plan' },
  { id: 'agency', label: 'Agency Coordination' },
  { id: 'hearings', label: 'Public Hearings' },
  { id: 'exec', label: 'Executive Management' },
];

export const Estimator = () => {
  const [acres, setAcres] = useState([120]);
  const [type, setType] = useState('residential');
  const [location, setLocation] = useState('ontario');
  const [status, setStatus] = useState('raw');
  const [phases, setPhases] = useState(['specific', 'agency', 'hearings']);
  const [showCapture, setShowCapture] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const result = useMemo(() => {
    const s = STATUSES.find(x => x.id === status)?.baseMonths ?? 12;
    // Size factor — larger sites take longer; tapers off above 250ac
    const sizeFactor = Math.min(2.0, 0.7 + acres[0] / 350);
    // Scope factor — each selected phase adds time
    const scopeFactor = 0.4 + phases.length * 0.16;
    const months = Math.max(3, Math.round(s * sizeFactor * scopeFactor / 1.2));
    return { months };
  }, [acres, status, phases]);

  const togglePhase = (id) => {
    setPhases(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  };

  const submitInquiry = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    setSubmitting(true);
    try {
      await createLead({
        source: "estimator",
        name: fd.get("name"),
        email: fd.get("email"),
        company: fd.get("company"),
        project_type: type,
        county: location,
        acres: acres[0],
        terrain: status, // reuse field for entitlement status
        phases,
        estimated_weeks: result.months * 4,
        message: `Entitlement inquiry · ${LOCATIONS.find(l=>l.id===location)?.label} · status: ${STATUSES.find(s=>s.id===status)?.label}`,
      });
      toast.success("Inquiry received. RCCD will be in touch within one business day.");
      setShowCapture(false);
    } catch (err) {
      toast.error("Couldn't send your inquiry. Try again or call (714) 637-4405.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="estimator" className="py-24 lg:py-40 bg-secondary border-y border-border">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
          <div className="max-w-2xl">
            <span className="mono text-[10px] uppercase tracking-[0.25em] text-clay flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5" /> Section · 06 — Entitlement Inquiry
            </span>
            <h2 className="font-display text-4xl lg:text-6xl text-ink mt-4 leading-[1.02] tracking-[-0.03em] text-balance">
              Scope your <em className="font-light">entitlement path.</em>
            </h2>
          </div>
          <p className="max-w-md text-foreground/70">Tell us about the parcel and where it is in the process. We'll come back with a phased plan and an honest read on timeline — typically within one business day.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
          {/* Inputs */}
          <div className="lg:col-span-7 bg-paper border border-border p-8 lg:p-12">
            <div className="space-y-10">
              {/* Acres slider */}
              <div>
                <div className="flex items-baseline justify-between">
                  <label className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Project size</label>
                  <span className="font-display text-4xl text-clay tabular-nums">{acres[0]}<span className="text-base text-muted-foreground ml-1">acres</span></span>
                </div>
                <Slider value={acres} onValueChange={setAcres} min={5} max={500} step={5} className="mt-4" />
                <div className="flex justify-between mono text-[10px] text-muted-foreground mt-2">
                  <span>5 ac</span><span>250 ac</span><span>500 ac</span>
                </div>
              </div>

              {/* Project type */}
              <div>
                <label className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Project type</label>
                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                  {TYPES.map(t => (
                    <button
                      key={t.id}
                      onClick={() => setType(t.id)}
                      className={`py-3 px-4 mono text-[10px] uppercase tracking-[0.22em] border text-left transition-colors ${
                        type === t.id ? 'bg-ink text-paper border-ink' : 'border-border hover:border-ink'
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Location */}
              <div>
                <label className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Location</label>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {LOCATIONS.map(l => (
                    <button
                      key={l.id}
                      onClick={() => setLocation(l.id)}
                      className={`py-3 px-4 mono text-[10px] uppercase tracking-[0.22em] border text-left transition-colors ${
                        location === l.id ? 'bg-ink text-paper border-ink' : 'border-border hover:border-ink'
                      }`}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Entitlement status */}
              <div>
                <label className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Current status</label>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {STATUSES.map(s => (
                    <button
                      key={s.id}
                      onClick={() => setStatus(s.id)}
                      className={`py-3 px-4 mono text-[10px] uppercase tracking-[0.22em] border text-left transition-colors ${
                        status === s.id ? 'bg-ink text-paper border-ink' : 'border-border hover:border-ink'
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Scope */}
              <div>
                <label className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Scope of support</label>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {PHASES.map(p => {
                    const sel = phases.includes(p.id);
                    return (
                      <button
                        key={p.id}
                        onClick={() => togglePhase(p.id)}
                        className={`flex items-center justify-between py-3 px-4 mono text-[10px] uppercase tracking-[0.22em] border transition-colors ${
                          sel ? 'bg-clay text-paper border-clay' : 'border-border hover:border-clay'
                        }`}
                      >
                        <span>{p.label}</span>
                        <span>{sel ? '✓' : '+'}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Result */}
          <div className="lg:col-span-5 bg-ink text-paper p-8 lg:p-12 flex flex-col grain relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-primary-glow/15 blur-3xl" />
            <div className="relative">
              <div className="mono text-[10px] uppercase tracking-[0.25em] text-primary-glow">Indicative timeline</div>
              <motion.div
                key={result.months}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="font-display text-5xl lg:text-7xl mt-4 leading-none tracking-[-0.03em]"
              >
                ~{result.months} <span className="text-3xl lg:text-4xl text-paper/70">mo.</span>
              </motion.div>
              <div className="mt-2 mono text-xs text-paper/60">From kickoff to approved framework · varies with agency</div>

              <div className="mt-10 grid grid-cols-2 gap-6 border-t border-paper/15 pt-6">
                <div>
                  <div className="mono text-[10px] uppercase tracking-[0.2em] text-paper/55">Scope phases</div>
                  <div className="font-display text-2xl text-paper mt-1">{phases.length}</div>
                </div>
                <div>
                  <div className="mono text-[10px] uppercase tracking-[0.2em] text-paper/55">Site size</div>
                  <div className="font-display text-2xl text-paper mt-1">{acres[0]} ac</div>
                </div>
              </div>

              <Button
                data-testid="estimator-lock-btn"
                onClick={() => setShowCapture(true)}
                className="mt-10 w-full h-12 rounded-none bg-paper text-ink hover:bg-primary-glow mono text-[11px] uppercase tracking-[0.22em]"
              >
                Send inquiry to RCCD
                <ArrowUpRight className="ml-2 h-4 w-4" />
              </Button>
              <p className="mt-4 mono text-[10px] text-paper/45 leading-relaxed">
                Indicative only. Actual timelines depend on jurisdiction, environmental review, and agency response. RCCD will give you a candid read after a brief conversation.
              </p>
            </div>
          </div>
        </div>

        {/* Lead capture modal */}
        <AnimatePresence>
          {showCapture && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[60] bg-ink/80 backdrop-blur-sm flex items-center justify-center p-4"
              onClick={() => setShowCapture(false)}
            >
              <motion.form
                initial={{ scale: 0.95, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.95, opacity: 0, y: 20 }}
                transition={{ duration: 0.25 }}
                onClick={(e) => e.stopPropagation()}
                onSubmit={submitInquiry}
                data-testid="estimator-modal"
                className="bg-paper text-ink max-w-lg w-full p-8 lg:p-10 relative grain"
              >
                <button type="button" onClick={() => setShowCapture(false)} className="absolute top-4 right-4 text-foreground/60 hover:text-clay" aria-label="Close">
                  <X className="h-5 w-5" />
                </button>
                <div className="mono text-[10px] uppercase tracking-[0.25em] text-clay">Send inquiry</div>
                <h3 className="font-display text-3xl mt-3 leading-tight">Where should RCCD follow up?</h3>
                <p className="mt-3 text-sm text-foreground/70">
                  We'll prepare a brief written response based on your inputs <span className="font-medium">({acres[0]} acres · {TYPES.find(t=>t.id===type)?.label} · {LOCATIONS.find(l=>l.id===location)?.label})</span> and reach out within one business day.
                </p>

                <div className="mt-7 space-y-4">
                  <Input data-testid="est-name" name="name" required placeholder="Your name" className="h-12 rounded-none border-0 border-b border-border bg-transparent px-0 text-base focus-visible:ring-0 focus-visible:border-clay" />
                  <Input data-testid="est-email" name="email" required type="email" placeholder="Email" className="h-12 rounded-none border-0 border-b border-border bg-transparent px-0 text-base focus-visible:ring-0 focus-visible:border-clay" />
                  <Input data-testid="est-company" name="company" placeholder="Company (optional)" className="h-12 rounded-none border-0 border-b border-border bg-transparent px-0 text-base focus-visible:ring-0 focus-visible:border-clay" />
                </div>

                <div className="mt-6 p-4 bg-secondary border border-border">
                  <div className="flex justify-between mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    <span>Indicative timeline</span>
                    <span>Phases</span>
                  </div>
                  <div className="flex justify-between font-display text-2xl mt-2">
                    <span>~{result.months} mo.</span>
                    <span>{phases.length} selected</span>
                  </div>
                </div>

                <Button data-testid="est-submit" type="submit" disabled={submitting} className="mt-7 w-full h-12 rounded-none bg-ink text-paper hover:bg-clay mono text-[11px] uppercase tracking-[0.22em]">
                  {submitting ? 'Sending…' : 'Send my inquiry'}
                  <ArrowUpRight className="ml-2 h-4 w-4" />
                </Button>
              </motion.form>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};
